import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import styles from './Pagination.module.css';

interface PaginationProps {
  page: number;
  pageCount: number;
  totalItems: number;
  onPageChange: (page: number) => void;
}

// Builds a compact page list with ellipses, e.g. 1 … 4 5 [6] 7 8 … 14
function getPageNumbers(page: number, pageCount: number): (number | 'ellipsis')[] {
  const pages: (number | 'ellipsis')[] = [];
  const add = (p: number) => pages.push(p);

  const windowStart = Math.max(2, page - 1);
  const windowEnd = Math.min(pageCount - 1, page + 1);

  add(1);
  if (windowStart > 2) pages.push('ellipsis');
  for (let p = windowStart; p <= windowEnd; p++) add(p);
  if (windowEnd < pageCount - 1) pages.push('ellipsis');
  if (pageCount > 1) add(pageCount);

  return pages;
}

export function Pagination({ page, pageCount, totalItems, onPageChange }: PaginationProps) {
  if (pageCount <= 1) return null;

  const pages = getPageNumbers(page, pageCount);

  return (
    <div className={styles.pagination}>
      <span className={styles.summary}>{totalItems} total</span>
      <div className={styles.controls}>
        <button
          className={styles.navBtn}
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label="Previous page"
        >
          <ChevronLeft size={16} />
        </button>
        {pages.map((p, i) =>
          p === 'ellipsis' ? (
            <span key={`ellipsis-${i}`} className={styles.ellipsis}>
              …
            </span>
          ) : (
            <button
              key={p}
              className={`${styles.pageBtn} ${p === page ? styles.pageBtnActive : ''}`}
              onClick={() => onPageChange(p)}
            >
              {p}
            </button>
          ),
        )}
        <button
          className={styles.navBtn}
          onClick={() => onPageChange(page + 1)}
          disabled={page >= pageCount}
          aria-label="Next page"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}
